import React from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, 
  AlertOctagon, 
  Clock, 
  Target, 
  X,
  Zap,
  Calendar,
  Bot,
  User,
  GitMerge,
  Copy
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { format, differenceInHours } from 'date-fns';

export default function TaskDiagnosticsPanel({ diagnostics, officers, onClose, onTaskClick }) {
  const getOfficer = (email) => officers.find(o => o.user_email === email);

  const categories = [
    {
      key: 'struggling',
      label: 'Struggling',
      icon: AlertOctagon,
      color: 'rose',
      description: 'Tasks marked as blocked or struggling',
      items: diagnostics.struggling
    },
    {
      key: 'dragging',
      label: 'Dragging',
      icon: Clock,
      color: 'amber',
      description: 'Tasks in progress for over 24 hours',
      items: diagnostics.dragging
    },
    {
      key: 'overdue',
      label: 'Overdue',
      icon: Calendar,
      color: 'rose',
      description: 'Tasks past their due date',
      items: diagnostics.overdue
    },
    {
      key: 'conflicting',
      label: 'Conflicting',
      icon: GitMerge,
      color: 'purple',
      description: 'Tasks flagged as overlapping with other tasks',
      items: diagnostics.conflicting || []
    },
    {
      key: 'redundant',
      label: 'Redundant',
      icon: Copy,
      color: 'cyan',
      description: 'Tasks flagged as redundant or duplicates',
      items: diagnostics.redundant || []
    },
    {
      key: 'unstarted',
      label: 'Unstarted',
      icon: Zap,
      color: 'slate',
      description: 'Tasks that haven\'t been started yet',
      items: diagnostics.unstarted
    },
    {
      key: 'noKRLink',
      label: 'No KR Link',
      icon: Target,
      color: 'amber',
      description: 'Tasks not linked to any Key Result',
      items: diagnostics.noKRLink
    }
  ];

  const activeCategories = categories.filter(c => c.items.length > 0);

  return (
    <div className="bg-slate-900/80 border border-amber-500/30 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider">Task Diagnostics</h3>
        </div>
        <Button 
          size="icon" 
          variant="ghost" 
          onClick={onClose}
          className="h-6 w-6 text-slate-500 hover:text-slate-300"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {activeCategories.length === 0 ? (
        <div className="text-center py-4 text-emerald-400 text-sm">
          ✓ No diagnostic issues found
        </div>
      ) : (
        <div className="max-h-[200px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded-full">
          <div className="space-y-4">
            {activeCategories.map(category => (
              <div key={category.key}>
                <div className="flex items-center gap-2 mb-2">
                  <category.icon className={cn("w-4 h-4", `text-${category.color}-400`)} />
                  <span className={cn("text-xs font-bold uppercase", `text-${category.color}-400`)}>
                    {category.label} ({category.items.length})
                  </span>
                </div>
                <p className="text-[10px] text-slate-500 mb-2">{category.description}</p>
                
                <div className="space-y-1.5">
                  {category.items.slice(0, 5).map(task => {
                    const officer = getOfficer(task.user_email);
                    const isBot = officer?.is_bot || officer?.custom_role === 'AI Officer';
                    
                    return (
                      <div 
                        key={task.id}
                        onClick={() => onTaskClick?.(task)}
                        className={cn(
                          "flex items-center gap-2 p-2 rounded text-xs cursor-pointer hover:bg-slate-800/50 transition-colors",
                          `bg-${category.color}-950/20 border border-${category.color}-900/30`
                        )}
                      >
                        <div className="w-5 h-5 rounded-full bg-slate-800 flex items-center justify-center shrink-0 overflow-hidden">
                          {officer?.avatar_url ? (
                            <img src={officer.avatar_url} className="w-full h-full object-cover" alt="" />
                          ) : isBot ? (
                            <Bot className="w-3 h-3 text-purple-400" />
                          ) : (
                            <User className="w-3 h-3 text-slate-500" />
                          )}
                        </div>
                        <span className="text-slate-300 truncate flex-1">{task.task_description}</span>
                        {task.date && (
                          <span className="text-[9px] text-slate-500 shrink-0">
                            {format(new Date(task.date + 'T00:00:00'), 'MMM d')}
                          </span>
                        )}
                        {(category.key === 'conflicting' || category.key === 'redundant') && task.ai_assessment?.linked_task_id && (
                          <Badge variant="outline" className={cn("text-[9px] h-4", category.key === 'conflicting' ? "border-purple-500/30 text-purple-400" : "border-cyan-500/30 text-cyan-400")}>
                            linked
                          </Badge>
                        )}
                        {category.key === 'dragging' && task.started_at && (
                          <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-400 h-4">
                            {differenceInHours(new Date(), new Date(task.started_at))}h
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                  
                  {category.items.length > 5 && (
                    <div className="text-[10px] text-slate-500 pl-2">
                      +{category.items.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}